import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatCLP, statusBadge, dateLabel, formatTracking } from "../../lib/format";
import { CircleNotch, CaretLeft, CaretRight, CalendarCheck } from "@phosphor-icons/react";

const PAGE_SIZE = 10;

export default function AdminAgenda() {
  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => { api.get("/admin/agenda").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);

  if (!rows) return <div className="flex justify-center py-24"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>;

  const sorted = [...rows].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const paged = sorted.slice(start, start + PAGE_SIZE);

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid="admin-agenda">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36]">Torre de control</span>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1 mb-4 flex items-center gap-2"><CalendarCheck size={26} className="text-[#FF5A36]" /> Agenda global</h1>
      <p className="text-sm text-zinc-500 mb-4">{sorted.length} traslados en curso en toda la plataforma.</p>

      {sorted.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-10 text-center text-zinc-500" data-testid="admin-agenda-empty">No hay traslados activos.</div>
      ) : (
        <>
          <div className="rounded-2xl border border-zinc-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-zinc-400 bg-zinc-50 border-b border-zinc-200">
                  <th className="px-4 py-3 font-bold">Fecha</th>
                  <th className="px-4 py-3 font-bold">N° Traslado</th>
                  <th className="px-4 py-3 font-bold">Ruta</th>
                  <th className="px-4 py-3 font-bold">Categoría</th>
                  <th className="px-4 py-3 font-bold">Transportista</th>
                  <th className="px-4 py-3 font-bold">Estado</th>
                  <th className="px-4 py-3 font-bold text-right">Monto</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const badge = statusBadge(r.status);
                  return (
                    <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`admin-agenda-row-${r.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap">{r.date ? dateLabel(r.date) : "—"} {r.timeRange || ""}</td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-600 whitespace-nowrap">{formatTracking(r.trackingNumber)}</td>
                      <td className="px-4 py-3 font-semibold whitespace-nowrap">{r.origin} → {r.destination}</td>
                      <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">{r.category}</td>
                      <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">{r.carrier || "—"}</td>
                      <td className="px-4 py-3"><span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${badge.color}`}>{badge.label}</span></td>
                      <td className="px-4 py-3 text-right font-bold whitespace-nowrap">{formatCLP(r.price)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3 flex-wrap gap-3" data-testid="admin-agenda-pagination">
            <span className="text-sm text-zinc-500">Mostrando {start + 1}–{Math.min(start + PAGE_SIZE, sorted.length)} de {sorted.length}</span>
            <div className="flex items-center gap-2">
              <button data-testid="admin-agenda-prev" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-300 text-sm font-semibold text-zinc-600 hover:border-[#0F1115] disabled:opacity-40 active:scale-95"><CaretLeft size={14} weight="bold" /> Anterior</button>
              <span className="text-sm font-semibold text-zinc-700">Página {currentPage} de {totalPages}</span>
              <button data-testid="admin-agenda-next" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-300 text-sm font-semibold text-zinc-600 hover:border-[#0F1115] disabled:opacity-40 active:scale-95">Siguiente <CaretRight size={14} weight="bold" /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
